import React, { useEffect, useState } from "react";
import { useStoreActions, useStoreState } from "ezStore";
import { intl } from "LocaleWrapper";

import usePropertyChangeEffect from "@cofano/cck/hooks/usePropertyChangeEffect";
import { Badge, Box, useTheme } from "@mui/material";
import ChatIcon from "@mui/icons-material/Chat";

import { buildGroups, CommentableEntity, hasUnreadComments, SelectedCommentableEntity } from "./utils";

import CommentsWidgetHeader from "./components/widget/CommentsWidgetHeader";
import CommentsWidgetGroup from "./components/widget/CommentsWidgetGroup";
import CommentsWidgetList from "./components/widget/CommentsWidgetList";
import CommentsWidgetForm from "./components/widget/CommentsWidgetForm";

import { CommentEntityType } from "__generated__/enums";

interface CommentsWidgetProps {
	collapsableDisabled?: boolean;
	entities: CommentableEntity[];
	onChange?: () => void;
	openCommentsByEntity?: SelectedCommentableEntity | null;
	onClose: () => void;
}

const CommentsWidget = ({ collapsableDisabled, entities, onChange, openCommentsByEntity, onClose }: CommentsWidgetProps) => {

	const readComments = useStoreState(store => store.session.readComments);
	const addReadComments = useStoreActions(actions => actions.session.addReadComments);

	const theme = useTheme();

	const [ open, setOpen ] = useState(false);
	const [ expandedGroups, setExpandedGroups ] = useState<CommentEntityType[]>([]);
	const [ selectedEntity, selectEntity ] = useState<CommentableEntity>();

	const hasUnread = hasUnreadComments([ ...entities ].flatMap(e => e.comments), readComments);
	const groups = buildGroups(entities);

	const handleOnReadComments = (entity: CommentableEntity) => {

		const unreadComments = entity.comments.filter(c => !readComments.comments.find(co => co.id === c.id));

		if (unreadComments.length > 0) {
			addReadComments(unreadComments);
		}
	};

	const handleOpenCommentsByEntity = () => {
		if (openCommentsByEntity) {
			setOpen(true);
			selectEntity(entities.find(e => e.id === openCommentsByEntity.id));

			const expanded = expandedGroups.find(it => it === openCommentsByEntity.type);

			if (!expanded) {
				setExpandedGroups([ ...expandedGroups, openCommentsByEntity.type ]);
			}
		}
	};

	useEffect(() => {
		handleOpenCommentsByEntity();
	}, [ openCommentsByEntity ]);

	useEffect(() => {
		if (selectedEntity) {
			handleOnReadComments(selectedEntity);
		}
	}, [ selectedEntity ]);

	useEffect(() => {
		if (collapsableDisabled && entities[0]) {
			selectEntity(entities[0]);
		}
	}, [ collapsableDisabled, entities.length ]);

	usePropertyChangeEffect(() => {
		if (!open) {
			setExpandedGroups([]);
			selectEntity(undefined);
		}
	}, [ open ]);

	return (
		<>
			{!open && (
				<Box
					onClick={() => setOpen(true)}
					sx={{
						alignItems: "center",
						backgroundColor: theme.palette.mode === "dark" ? "primary.main" : "#072035",
						bottom: 0,
						borderTopLeftRadius: 4,
						cursor: "pointer",
						display: "flex",
						height: 32,
						justifyContent: "center",
						position: "fixed",
						right: 0,
						transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
						width: 32,
						zIndex: 10000,

						"&:hover": {
							backgroundColor: theme.palette.mode === "dark" ? "primary.dark" : "#0c3556",
							height: 38,
							width: 38,
						},

					}}>
					<Badge color="error" variant="dot" invisible={!hasUnread}>
						<ChatIcon sx={{ color: "#FFF", fontSize: 14 }} />
					</Badge>
				</Box>
			)}

			<Box
				sx={{
					bottom: 0,
					boxShadow: open ? "-8px 0px 24px rgba(0, 0, 0, 0.08)" : "none",
					display: "flex",
					height: 548,
					flexDirection: "column",
					position: "fixed",
					right: 0,
					transition: "transform 0.4s cubic-bezier(0.16, 1, 0.3, 1)",
					transform: open ? "translateX(0)" : "translateX(100%)",
					width: 700,
					zIndex: 10000,
				}}>
				<CommentsWidgetHeader
					onClose={() => {
						setOpen(false);
						onClose();
					}} />

				<Box
					sx={{
						display: "flex",
						height: 500,
					}}>
					<Box
						sx={{
							backgroundColor: "background.paper",
							borderRight: "1px solid",
							borderColor: "divider",
							overflow: "auto",
							width: 260,
						}}>
						{Object.keys(groups).map((key, index) => {
							const entityType = key as CommentEntityType;

							if (groups[entityType].length > 0) {
								return (
									<CommentsWidgetGroup
										title={intl.formatMessage({ id: entityType })}
										collapsableDisabled={collapsableDisabled}
										expanded={expandedGroups.includes(entityType)}
										onToggle={() => {
											const expanded = expandedGroups.includes(entityType);

											if (expanded) {
												setExpandedGroups([ ...expandedGroups ].filter(it => it !== entityType));
											} else {
												setExpandedGroups([ ...expandedGroups, entityType ]);
											}
										}}
										entities={groups[entityType]}
										selectedEntity={selectedEntity}
										selectEntity={selectEntity}
										readComments={readComments}
										key={index} />
								);
							} else {
								return (
									<React.Fragment />
								);
							}
						})}
					</Box>

					<Box
						sx={{
							bgcolor: theme.palette.mode === "dark" ? "background.paper" : "#EEE",
							display: "flex",
							flexDirection: "column",
							height: "100%",
							width: 440,
						}}>
						{selectedEntity && (
							<Box
								sx={{
									borderBottom: "1px solid",
									borderColor: "divider",
									color: "text.primary",
									fontWeight: 600,
									fontSize: 14,
									p: 2,
									width: "100%",
								}}>
								{selectedEntity.title}
							</Box>
						)}

						<Box
							sx={{
								flexGrow: 1,
								overflowY: "auto",
								pl: 2,
								pt: 2,
								pr: 2,
								pb: 0,
							}}>
							{selectedEntity && (
								<CommentsWidgetList entity={selectedEntity} />
							)}
						</Box>

						<Box
							sx={{
								p: 2,
								borderTop: "1px solid",
								borderColor: "divider",
							}}>
							<CommentsWidgetForm
								entity={selectedEntity}
								onSave={comment => {
									addReadComments([ { id: comment.id, created: comment.created } ]);

									if (selectedEntity) {
										selectEntity({
											...selectedEntity,
											comments: [ ...selectedEntity.comments, comment ],
										});
									}

									onChange?.();
								}}
							/>
						</Box>
					</Box>
				</Box>
			</Box>
		</>
	);
};

export default CommentsWidget;
